'use client';

import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { ViberLink } from '@/components/ui/viber-link';
import { WhatsAppBrandMark } from '@/components/ui/whatsapp-icon';
import { WhatsAppLink } from '@/components/ui/whatsapp-link';
import { cn } from '@/lib/utils';

interface MessagingCtaButtonsProps {
  whatsappLabel: string;
  whatsappAria: string;
  /** Defaults to common.footer.viber */
  viberLabel?: string;
  /** Defaults to common.phoneReveal.viberLabel */
  viberAria?: string;
  /** Shared outline button classes (page-specific contrast) */
  buttonClassName?: string;
  /** Per-button overrides — default to buttonClassName when unset */
  whatsappClassName?: string;
  viberClassName?: string;
  showIcons?: boolean;
  className?: string;
}

/**
 * Shared WhatsApp + Viber contact CTAs used wherever messaging contact is offered.
 */
export function MessagingCtaButtons({
  whatsappLabel,
  whatsappAria,
  viberLabel,
  viberAria,
  buttonClassName,
  whatsappClassName,
  viberClassName,
  showIcons = true,
  className,
}: MessagingCtaButtonsProps) {
  const tFooter = useTranslations('common.footer');
  const tReveal = useTranslations('common.phoneReveal');

  const resolvedViberLabel = viberLabel ?? tFooter('viber');
  const resolvedViberAria = viberAria ?? tReveal('viberLabel');

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap',
        className,
      )}
    >
      <Button
        variant='outline'
        size='lg'
        className={cn('min-h-11', buttonClassName, whatsappClassName)}
        asChild
      >
        <WhatsAppLink ariaLabel={whatsappAria}>
          <span className='inline-flex items-center gap-2'>
            {showIcons ? <WhatsAppBrandMark className='h-4 w-4' /> : null}
            {whatsappLabel}
          </span>
        </WhatsAppLink>
      </Button>
      <Button
        variant='outline'
        size='lg'
        className={cn('min-h-11', buttonClassName, viberClassName)}
        asChild
      >
        <ViberLink ariaLabel={resolvedViberAria}>
          <span className='inline-flex items-center gap-2'>
            {showIcons ? (
              <img
                src='/images/Logos/viber.svg'
                alt=''
                width={16}
                height={16}
                className='h-4 w-4'
                aria-hidden
              />
            ) : null}
            {resolvedViberLabel}
          </span>
        </ViberLink>
      </Button>
    </div>
  );
}
